import {
    PatientRecord,
    ChatCompletions,
    DocumentType,
    SectionFormatting,
    DocumentStatus,
    MultiPatientData,
    TranscriptSection,
    TranscriptSectionStatus,
    PatientStage,
    DocumentAsset,
    DocumentAssetStatus,
    AccountStatus,
    CategoryAggregate,
    VisitSentimentAnalysis,
} from "@common/utils/types";
import { cloneDeep } from "lodash";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";

export const toTitleCase = (str: string): string => {
    return str
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

export function transformTableRecordToPatientRecord(patientRecordData: PatientRecord): PatientRecord {
    const {
        id,
        doctors,
        patient_id,
        patient_name,
        status,
        appointment_duration,
        scheduled_at,
        created_at,
        stage,
        resource_id,
    } = patientRecordData;
    const doctor_id = doctors?.id || null;

    return {
        id,
        doctor_id,
        patient_id,
        patient_name,
        status,
        appointment_duration,
        scheduled_at,
        created_at,
        stage,
        resource_id,
    };
}

/**
 * Formats a UTC/offset ISO string for <input type="datetime-local" /> (local wall time, no Z).
 */
export const formatDateForInput = (d: string) => {
    const date = DateTime.fromISO(d);
    if (!date.isValid) {
        return "";
    }
    return date.toLocal().toFormat("yyyy-MM-dd'T'HH:mm");
};

/**
 * Formats a time string with uppercase AM/PM (matching moment.js "h:mm A" behavior).
 */
export const formatTime = (d: string) => {
    const date = DateTime.fromISO(d, { zone: "local" });
    if (!date.isValid) {
        return "";
    }
    return date.toFormat("h:mm a").toUpperCase();
};

/**
 * Formats a date string for display.
 */
export const formatDateAndTime = (d: string) => {
    const date = DateTime.fromISO(d, { zone: "local" });
    if (!date.isValid) {
        return "";
    }
    return date.toFormat("M/d/yyyy, h:mm a").toUpperCase();
};

/**
 * Formats a date string for display in month day year format.
 */
export const formatDateToMonthDayYear = (d: string) => {
    const date = DateTime.fromISO(d, { zone: "local" });
    if (!date.isValid) {
        return "";
    }
    return date.toFormat("MMMM dd, yyyy");
};

/**
 * For appointment / visit pickers: local date, start time, and duration on one line.
 */
export const formatVisitScheduleSummary = (scheduledAtIso: string, durationMinutes?: number | null) => {
    const dt = DateTime.fromISO(scheduledAtIso);
    if (!dt.isValid) {
        return "";
    }
    const local = dt.toLocal();
    const datePart = local.toFormat("MMMM dd, yyyy");
    const timePart = local.toFormat("h:mm a");
    const mins = durationMinutes != null && Number.isFinite(Number(durationMinutes)) ? Number(durationMinutes) : 30;
    return `${datePart} · ${timePart} · ${mins} min`;
};

/**
 * Checks if two dates are on the same day.
 */
export const isSameDay = (date1: Date, date2: Date) => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
};

/**
 * Formats a date as a zero-padded local YYYY-MM-DD string, for use as a `<time dateTime>`
 * attribute. Uses local getters (not toISOString, which converts to UTC and can shift the
 * date across midnight in negative-offset timezones).
 */
export const toLocalDateString = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export function isSameWeek(date1: Date, date2: Date | null): boolean {
    if (!date2) return false;
    const firstDate = new Date(date1);
    const secondDate = new Date(date2);
    const firstWeek = getWeekRange({ startDate: firstDate, endDate: firstDate });
    const secondWeek = getWeekRange({ startDate: secondDate, endDate: secondDate });

    if (!firstWeek.startDate || !secondWeek.startDate) return false;

    return isSameDay(firstWeek.startDate, secondWeek.startDate);
}

export const timeAgo = (date: string) => {
    return DateTime.fromISO(date, { zone: "local" }).toRelative() || "";
};

export function createNewAssistantMessage(
    documentId: string,
    header: string,
    patientRecordId: string,
    transcriptionId: string,
    isJson: boolean = false,
    document: DocumentType | null,
    document_snapshot: SectionFormatting | null,
) {
    let newAssistantMessage: ChatCompletions | null = null;
    const newMessageId = uuidv4();
    newAssistantMessage = {
        id: newMessageId,
        role: "assistant",
        chat_json: {
            header: header,
            chat_text: null,
        },
        document_type_id: documentId,
        created_at: new Date().toISOString(),
        patient_record_id: patientRecordId,
        transcription_id: transcriptionId,
        document_types: {
            is_json: isJson,
            document_type_name: document?.document_type_name,
            creator_prompt_components: document?.creator_prompt_components,
        },
        document_snapshot: document_snapshot,
        header: header,
        status: DocumentStatus.Processing,
        chat_json_original: {
            header: header,
            chat_text: null,
        },
    } as ChatCompletions;

    return newAssistantMessage;
}

export function createTabId(documentId: string, header: string) {
    return `${documentId}_${header}`;
}

export function cloneDocument(document: ChatCompletions, newChatText: string, status: DocumentStatus) {
    const documentClone = cloneDeep(document);

    if (documentClone && documentClone?.chat_json) {
        documentClone.chat_json = {
            ...documentClone.chat_json,
            chat_text: newChatText,
        };

        documentClone.status = status;
    }

    return documentClone;
}

/**
 * Determines the chat header and patient for a multiple patient case.
 * @param selectedMultiPatient - The selected patient name.
 * @param multiplePatients - An array of MultiPatientData objects representing multiple patients.
 * @param originalHeader - The original chat header.
 * @returns An object containing the updated header, a flag indicating if it's a multiple patient case, and the patient name to parse.
 */
export function determineChatHeaderAndPatientForMultiplePatientCase(
    selectedMultiPatient: string | null,
    multiplePatients: MultiPatientData[],
    originalHeader: string,
): { header: string; multiplePatientCase: boolean; patientNameToParse: string } {
    // Test original header to see if we're in a retry case
    const patientMatch = multiplePatients.find((patient: MultiPatientData) => {
        const regex = new RegExp(`\\(${patient.name}\\)`);
        return regex.test(originalHeader);
    });

    //Test or retry and patient match in original header
    if (patientMatch) {
        return {
            header: originalHeader,
            multiplePatientCase: true,
            patientNameToParse: patientMatch.name,
        };
        //Test whether or not we have a selected patient and are generating from Generate Documents
        //even if they're not still selected they need to be able to be re-ran
    } else if (selectedMultiPatient && multiplePatients?.length >= 2) {
        return {
            header: `${originalHeader}(${selectedMultiPatient})`,
            multiplePatientCase: true,
            patientNameToParse: selectedMultiPatient,
        };
        //No multiple patients
    } else {
        return {
            header: originalHeader,
            multiplePatientCase: false,
            patientNameToParse: "",
        };
    }
}

export const isTranscriptError = (transcriptSection: TranscriptSection) =>
    transcriptSection.status === TranscriptSectionStatus.FailedIncompleteUpload ||
    transcriptSection.status === TranscriptSectionStatus.FailedErrorProcessing ||
    transcriptSection.status === TranscriptSectionStatus.FailedBlankTranscript ||
    transcriptSection.status === TranscriptSectionStatus.FailedIrrelevantInformation ||
    transcriptSection.status === TranscriptSectionStatus.FailedPartialTranscription ||
    (transcriptSection.status !== TranscriptSectionStatus.InProgress &&
        transcriptSection.status !== TranscriptSectionStatus.Uploaded &&
        transcriptSection.status !== TranscriptSectionStatus.NotStarted &&
        transcriptSection?.data?.trim() === "");

const STALE_THRESHOLD_MS = 15 * 60 * 1000;

/**
 * Returns the number of milliseconds until a section becomes stale, or 0 if already stale.
 * Returns -1 if the section is not in a stale-eligible status.
 * @param lastRetryAt - optional local-only timestamp from a successful retry (not stored in Redux/DB)
 */
export function msUntilStale(transcriptSection: TranscriptSection | null, lastRetryAt?: string): number {
    if (!transcriptSection?.created_at) return -1;
    const status = transcriptSection?.status;
    if (status !== TranscriptSectionStatus.NotStarted && status !== TranscriptSectionStatus.Uploaded) return -1;
    const createdAt = new Date(transcriptSection.created_at).getTime();
    if (isNaN(createdAt)) return -1; // Invalid date string — treat as not stale-eligible
    const retryAt = lastRetryAt ? new Date(lastRetryAt).getTime() : 0;
    const baseline = Math.max(createdAt, isNaN(retryAt) ? 0 : retryAt);
    return Math.max(0, baseline + STALE_THRESHOLD_MS - Date.now());
}

/**
 * Checks if a transcript section is stale (stuck in processing for >15 minutes).
 * @param lastRetryAt - optional local-only timestamp from a successful retry
 */
export function isStaleSection(transcriptSection: TranscriptSection | null, lastRetryAt?: string): boolean {
    return msUntilStale(transcriptSection, lastRetryAt) === 0;
}

/**
 * Determines the error state of a transcript section.
 * @param transcriptSection - The transcript section to check.
 * @param documentCreationTime - The creation time of the document.
 * @returns The error state of the transcript section. as 'error', 'warning', or 'none'.
 */
export function determineErrorState(
    transcriptSection: TranscriptSection | null,
    chatCompletions: ChatCompletions[],
): "error" | "warning" | "none" {
    if (!transcriptSection?.status) return "none";

    if (isStaleSection(transcriptSection)) return "error";

    if (
        transcriptSection?.status === TranscriptSectionStatus.InProgress ||
        transcriptSection?.status === TranscriptSectionStatus.Uploaded ||
        transcriptSection?.status === TranscriptSectionStatus.NotStarted
    )
        return "none";
    if (isTranscriptError(transcriptSection)) return "error";

    if (chatCompletions.every((chatCompletion) => chatCompletion?.transcript_section_ids)) {
        const tsUsed = chatCompletions.some((chatCompletion) =>
            chatCompletion?.transcript_section_ids?.includes(transcriptSection?.id),
        );

        if (!tsUsed) return "warning";
    }

    return "none";
}

/*
 * Determines the warning state of a document asset.
 * @param documentAsset - The document asset to check.
 * @returns The warning state of the document asset as 'warning', or 'none'.
 */
export function determineErrorStateFile(
    documentAsset: DocumentAsset,
    chatCompletions: ChatCompletions[],
    hasNonJsonDocument?: boolean,
) {
    if (hasNonJsonDocument) return "error";

    // if the document asset is failed, return error
    if (documentAsset.status === DocumentAssetStatus.Failed) return "error";

    // verify the chat completions have document_asset_ids
    if (!chatCompletions.every((chatCompletion) => chatCompletion?.document_asset_ids)) return "none";

    // iterate through the chat completions and check if the document asset is used in any of them
    const documentAssetUsed = chatCompletions.some((chatCompletion) =>
        chatCompletion?.document_asset_ids?.includes(documentAsset.id),
    );

    if (documentAssetUsed) return "none";

    return "warning";
}

/**
 * Determines the error message for a transcript section.
 * @param TranscriptStatus - The status of the transcript section.
 * @returns The error message for the transcript section.
 */
export function determineErrorMessage(transcriptSection: TranscriptSection): string {
    const transcriptStatus = transcriptSection?.status;

    // Check for stale sections stuck in processing
    if (isStaleSection(transcriptSection)) {
        return "Processing appears stuck. Try retrying transcript processing.";
    }

    switch (transcriptStatus) {
        case TranscriptSectionStatus.FailedIncompleteUpload:
            return "There was a problem uploading the clip. Check Incomplete Uploads";
        case TranscriptSectionStatus.FailedErrorProcessing:
            return "There was an issue creating the transcript. Retry transcript processing.";
        case TranscriptSectionStatus.FailedBlankTranscript:
            return "Transcript contains no content. Check for valid audio and retry.";
        case TranscriptSectionStatus.FailedIrrelevantInformation:
            return "Transcript does not contain enough relevant information.";
        case TranscriptSectionStatus.FailedPartialTranscription:
            return "There was a problem uploading part of the clip. Check Incomplete Uploads";
        default:
            return "";
    }
}

/**
 * Determines if a string is a valid email address.
 * @param email - The email address to check.
 * @returns Whether the email address is valid.
 */
export function isValidEmail(email: string): boolean {
    // Regex explanation:
    // ^        - Start of string
    // [^\s@]+  - One or more characters that are not whitespace or @ symbol
    // @        - Literal @ symbol
    // [^\s@]+  - One or more characters that are not whitespace or @ symbol
    // \.       - Literal dot (period)
    // [^\s@]+  - One or more characters that are not whitespace or @ symbol
    // $        - End of string
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/** DOM id / URL hash for Settings → General → Client Communications Email */
export const CLIENT_COMMUNICATIONS_EMAIL_SECTION_ID = "client-communications-email" as const;

/** React Router `to` value: Settings → Send Emails from HappyDoc → Client Communications Email.
 *  Was `?tab=general` until the card moved to its own tab (EN-1056). Settings still aliases the old
 *  `?tab=general#client-communications-email` form, so links already sent to customers keep working. */
export const SETTINGS_CLIENT_COMMUNICATIONS_EMAIL_TO =
    `/settings?tab=send-emails#${CLIENT_COMMUNICATIONS_EMAIL_SECTION_ID}` as const;

export type DocumentSendingTooltipLink = {
    readonly to: typeof SETTINGS_CLIENT_COMMUNICATIONS_EMAIL_TO;
    readonly linkText: string;
};

/**
 * Determines if document sending is enabled based on validation rules.
 * @param sentAt - The sent_at timestamp from the document
 * @param communicationsEnabled - Array of enabled communication types
 * @param communicationsData - Array of communication records
 * @returns Object with isEnabled boolean, tooltip message, and optional settings deep link
 */
export function isDocumentSendingEnabled(
    sentAt: string | null | undefined,
    communicationsEnabled: string[] | null | undefined,
    communicationsData: any[] | undefined,
    _accountStatus: AccountStatus | null | undefined,
): { isEnabled: boolean; tooltipMessage: string; tooltipLink?: DocumentSendingTooltipLink } {
    // Check for a valid primary communication email
    const primaryEmail = communicationsData?.find(
        (comm) => comm.communication_type === "email" && comm.is_primary && comm.confirmed_at,
    );

    // Check if there is any email communication
    const hasAnyEmail = communicationsData?.some((comm) => comm.communication_type === "email");

    const isCommActive = communicationsData?.some((comm) => comm.is_active);

    // Check if email communication is enabled
    const emailEnabled = communicationsEnabled?.includes("email") && isCommActive;

    // If active, but no valid/verified email
    if (!primaryEmail) {
        if (hasAnyEmail) {
            return {
                isEnabled: false,
                tooltipMessage: "Validate email to enable document sending.",
            };
        }
        return {
            isEnabled: false,
            tooltipMessage: "Add and validate a Client Communication email.",
            tooltipLink: {
                to: SETTINGS_CLIENT_COMMUNICATIONS_EMAIL_TO,
                linkText: "Open Client Communications Email settings",
            },
        };
    }

    // If active, valid email, but email communication not enabled
    if (!emailEnabled) {
        return {
            isEnabled: false,
            tooltipMessage: "Email communication is not enabled.",
        };
    }

    return {
        isEnabled: true,
        tooltipMessage: sentAt ? `Last sent: ${new Date(sentAt).toLocaleDateString()}` : "Send Document",
    };
}

/**
 * determines the color of the stage based on the stage value
 * @param stage - The stage value
 * @returns The color of the stage
 */
export function renderStageColor(stage: PatientStage): string {
    switch (stage) {
        case PatientStage.Ongoing:
            return "#2EDEE5";

        case PatientStage.Review:
            return "#FCB718";

        case PatientStage.Finalized:
            return "#2D394D";

        default:
            return "#2EDEE5";
    }
}

/**
 * Computes a translucent background tint for a PIMS-native appointment-type color, or
 * `undefined` when there's nothing meaningful to show — null/undefined, a malformed value,
 * or Cornerstone's own un-set default of white (#FFFFFF), which would otherwise paint every
 * un-configured appointment type as a jarring white block.
 * @param pimsTypeColor - a "#RRGGBB" hex string (patient_record.pims_type_color), or null/undefined
 * @returns an rgba() background string, or undefined to let the caller keep its own default background
 */
export function renderPimsColorTint(pimsTypeColor?: string | null): string | undefined {
    if (!pimsTypeColor) return undefined;
    const hex = pimsTypeColor.trim().replace("#", "");
    if (hex.toUpperCase() === "FFFFFF") return undefined;
    if (!/^[0-9a-f]{6}$/i.test(hex)) return undefined;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.18)`;
}

/**
 * Given a selected date, returns the week range based on the selected date.
 * @param selectedDate
 * @returns Week range based on the selected date
 */
export function getWeekRange(selectedDate: { startDate: Date | null; endDate: Date | null }): {
    startDate: Date | null;
    endDate: Date | null;
} {
    // Ensure the input is a valid Date object
    if (!selectedDate?.["startDate"]) {
        return selectedDate;
    }

    // Clone the date to avoid mutating the original date
    const date = new Date(selectedDate["startDate"]);

    // Get the day of the week (0 for Sunday, 1 for Monday, etc.)
    const dayOfWeek = date.getDay();

    // Calculate the start and end dates
    const startDate = new Date(date);
    startDate.setDate(date.getDate() - dayOfWeek);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    return {
        startDate,
        endDate,
    };
}

/**
 * Calculates the age string of a patient using DOB
 *
 * `dob` is a date-only value and must always be read with `zone: "utc"`,
 * matching every other read site. See EN-998.
 *
 * Read as UTC, not because the stored time-of-day is always 00:00 - about
 * 29% of production rows carry a clinic-midnight offset instead
 * (04:00/05:00/06:00Z), written by the two server-side paths that anchor
 * DOB to the clinic zone. A UTC read is correct for those too, since
 * midnight in any negative-offset zone falls on the same UTC calendar day.
 *
 * Note this specific line has no *current* behavioral effect: Luxon's
 * `DateTime.diff()` re-anchors the "end" operand (birthDate) into the
 * "start" operand's (today's) zone via their shared instant, discarding
 * whatever zone birthDate was tagged with - so `zone: "local"` vs
 * `zone: "utc"` produces byte-identical output here (verified empirically,
 * 1464 sampled dates, 0 differences). The change is made anyway for
 * consistency with the other 4 read sites, and - more importantly - to
 * guard against a future refactor: the API-Server's sibling `calculateAge`
 * (`app/domain/utils.ts`) is built on native `Date` getters
 * (getFullYear/getMonth-style component arithmetic) rather than `.diff()`,
 * and *is* genuinely zone-sensitive. If this function is ever rewritten
 * the same way, the zone stops being inert and becomes load-bearing
 * immediately - a stray "local" here would silently reintroduce the
 * off-by-one with no other code change required to trigger it.
 * @param dob - Date of birth string in ISO format
 * @returns Formatted age string (e.g. "7m" or "2y 3m")
 */
export function calculateAge(dob: string | undefined): string {
    if (!dob) return "";
    const birthDate = DateTime.fromISO(dob, { zone: "utc" });
    if (!birthDate.isValid) return "";
    const today = DateTime.now();
    const diff = today.diff(birthDate, ["years", "months"]);
    const years = Math.floor(diff.years);
    const months = Math.floor(diff.months % 12);
    if (years > 0) {
        return `${years}y ${months}m`;
    }
    return `${months}m`;
}

// capitalize the first letter of a string
export function capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * aggregate the filtered visits into a CategoryAggregate array
 * for each sentiment category.
 * @param visits - The sentiment analysis data to aggregate.
 * @returns The aggregated sentiment category data.
 */
export function buildSentimentCategoryAggregates(
    visits: VisitSentimentAnalysis[],
    categories: string[],
): CategoryAggregate[] {
    // we want to accumulate ratings into buckets: 1, 2, 3, 4, 5 plus an average.
    const result: CategoryAggregate[] = [];

    categories.forEach((category) => {
        let oneCount = 0;
        let twoCount = 0;
        let threeCount = 0;
        let fourCount = 0;
        let fiveCount = 0;
        let totalRating = 0;
        let totalCount = 0;

        visits.forEach((visit) => {
            const rating = visit.ratings[category as keyof VisitSentimentAnalysis["ratings"]];
            // Bucket assignment
            switch (rating) {
                case 1:
                    oneCount++;
                    break;
                case 2:
                    twoCount++;
                    break;
                case 3:
                    threeCount++;
                    break;
                case 4:
                    fourCount++;
                    break;
                case 5:
                    fiveCount++;
                    break;
                default:
                    break;
            }

            // only count valid ratings
            if (typeof rating === "number" && rating >= 1 && rating <= 5) {
                totalRating += rating;
                totalCount++;
            }
        });

        const avg = totalCount > 0 ? totalRating / totalCount : 0;

        // convert the category key to a nicer label (e.g. "Empathy" from "empathy").
        // const label = category.charAt(0).toUpperCase() + category.slice(1);

        result.push({
            category: category,
            summary: "-",
            oneCount,
            twoCount,
            threeCount,
            fourCount,
            fiveCount,
            average: avg,
        });
    });

    return result;
}

/**
 * start sentry replay
 */
export const startSentryReplay = () => {
    const replay = Sentry.getReplay();
    if (replay) {
        replay.start();
    }
};

/**
 * stop sentry replay
 */
export const stopSentryReplay = () => {
    const replay = Sentry.getReplay();

    if (replay) {
        replay.stop();
    }
};

/**
 * Determines if all transcript sections are unusable.
 * @param transcriptSections - The transcript sections to check.
 * @returns Whether the transcript section is unusable.
 */
export function transcriptSectionsAreUnusable(transcriptSections: TranscriptSection[]): boolean {
    return transcriptSections.every((section) => ["loading", "", "error"].includes(section?.data || ""));
}

/*
 * @deprecated Use the useTemporaryDisable hook instead for React components.
 * This function directly manipulates the DOM and bypasses React's state management.
 *
 * Disables an element for a given timeout
 * @param element - The element to disable
 * @param timeout - The timeout in milliseconds
 * @param disabled - The disabled state to set the element to after the timeout
 */
export function disableElementTimeout(element: HTMLButtonElement, timeout: number) {
    element.disabled = true;
    setTimeout(() => {
        element.disabled = false;
    }, timeout);
}

type JoinPathStrings<T extends readonly string[]> = T extends readonly [
    infer F extends string,
    ...infer R extends readonly string[],
]
    ? `${F}${R extends [] ? "" : `/${JoinPathStrings<R>}`}`
    : "";
/**
 * Joins paths with a single slash
 * @param paths - The paths to join
 * @returns The joined paths
 */
export function pathJoin<T extends readonly string[]>(...paths: T): JoinPathStrings<T> {
    // join the paths and replace duplicated slashes with a single slash
    return paths.join("/").replace(/\/+/g, "/") as JoinPathStrings<T>;
}

export interface SafeErrorInfo {
    message: string;
    details?: { name: string; stack: string | undefined };
}

/**
 * Normalizes an error into a safe error info object
 * @param error - The error to normalize
 * @returns The normalized error info
 */
export function getSafeErrorInfo(error: unknown): SafeErrorInfo {
    if (error instanceof Error) {
        return {
            message: error.message,
            details: { name: error.name, stack: error.stack },
        };
    }
    return { message: String(error) };
}

/**
 * Returns a user-friendly error message for audio upload/transcription failures.
 * Replaces opaque internal errors with a generic message while preserving meaningful ones.
 * @param err - The error to format
 * @returns User-friendly error message
 */
export function getUserFriendlyErrorMessage(err: any): string {
    if (!err) return "Failed to upload and transcribe audio file";

    const message = err instanceof Error ? err.message : String(err);

    const trimmedMessage = message.trim();
    const normalizedMessage = trimmedMessage.toLowerCase();

    // Only collapse known opaque errors when the whole message matches.
    // This preserves backend detail in wrapped errors that merely include these tokens.
    const opaqueExactPatterns = new Set([
        "failed to fetch",
        "typeerror: failed to fetch",
        "authretryablefetcherror",
        "autherror",
        "networkerror",
        "fetch failed",
        "indexeddb",
        "failed_incomplete_upload",
        "failed_error_processing",
        "failed_blank_transcript",
        "failed_irrelevant_information",
        "failed_partial_transcription",
    ]);

    const isOpaqueError = opaqueExactPatterns.has(normalizedMessage);

    if (isOpaqueError) {
        return "Failed to upload and transcribe audio file";
    }

    return message;
}
